import type { Position, ScheduleSettings, Shift, User } from '../models/data';
import type { SchedulerDailyContextMap, SchedulerDailyNoteMode } from './dailyContext';

export const SCHEDULER_BOOTSTRAP_SCHEMA_VERSION = 1 as const;

export type SchedulerBootstrapVisibleScope = {
  fromDateKey: string;
  toDateKey: string;
  weekStartDateKey: string;
  noteMode: SchedulerDailyNoteMode;
};

export type SchedulerBootstrapScope = SchedulerBootstrapVisibleScope & {
  userId: string;
  unitIds: string[];
  divisionId?: string | null;
};

export type SchedulerBootstrapPermission = {
  canRead: boolean;
  canWrite: boolean;
};

export type SchedulerBootstrapPayload = {
  schemaVersion: typeof SCHEDULER_BOOTSTRAP_SCHEMA_VERSION;
  scopeKey: string;
  userId: string;
  unitIds: string[];
  divisionId: string | null;
  fromDateKey: string;
  toDateKey: string;
  weekStartDateKey: string;
  noteMode: SchedulerDailyNoteMode;
  /** Numeric version persisted on the current user's access profile. */
  accessProfileVersion: number;
  /** Opaque server fingerprint of every effective authorization source for this scope. */
  permissionVersion: string;
  /** Server baseline captured immediately before expensive bootstrap reads. */
  pollBaselineAt?: string;
  scheduleRevision: string;
  generatedAt: string;
  degradedSections?: string[];
  permissions: SchedulerBootstrapPermission & {
    byUnitId: Record<string, SchedulerBootstrapPermission>;
  };
  accessModeByUnitId: Record<
    string,
    'native' | 'cross_unit_temporary' | 'denied'
  >;
  delegatedPublishedOnlyUnitIds: string[];
  userDirectoryPermissions: SchedulerBootstrapPermission;
  shifts: Shift[];
  /** Published shifts from the four weeks preceding the requested scheduler week. */
  timePresetUsageShifts?: Shift[];
  users: User[];
  usersDirectoryDenied: boolean;
  positions: Position[];
  displaySettings: {
    orderedUserIds: string[];
    hiddenUserIds: string[];
  } | null;
  weekSettingsByUnitId: Record<string, ScheduleSettings>;
  dailyContextByDateKey: SchedulerDailyContextMap;
};

const normalizeString = (value?: string | null): string =>
  typeof value === 'string' ? value.trim() : '';

const SCHEDULER_DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const SCHEDULER_DAY_MS = 24 * 60 * 60 * 1000;

const parseSchedulerDateKeyUtc = (dateKey: string): Date => {
  const normalized = normalizeString(dateKey);
  const match = SCHEDULER_DATE_KEY_PATTERN.exec(normalized);
  if (!match) throw new Error(`INVALID_SCHEDULER_DATE_KEY:${dateKey}`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    throw new Error(`INVALID_SCHEDULER_DATE_KEY:${dateKey}`);
  }
  return date;
};

const formatSchedulerDateKeyUtc = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

export const shiftSchedulerDateKeyByDays = (dateKey: string, dayOffset: number): string => {
  if (!Number.isInteger(dayOffset)) throw new Error('SCHEDULER_DAY_OFFSET_MUST_BE_INTEGER');
  const shifted = new Date(parseSchedulerDateKeyUtc(dateKey).getTime() + dayOffset * SCHEDULER_DAY_MS);
  return formatSchedulerDateKeyUtc(shifted);
};

/**
 * Shift an exact scheduler bootstrap window by full weeks while retaining the
 * authenticated user, unit, division and draft/published scope.
 */
export const shiftSchedulerBootstrapScopeByWeeks = (
  scope: SchedulerBootstrapScope,
  weekOffset: number
): SchedulerBootstrapScope => {
  if (!Number.isInteger(weekOffset)) throw new Error('SCHEDULER_WEEK_OFFSET_MUST_BE_INTEGER');
  const dayOffset = weekOffset * 7;
  return {
    ...scope,
    unitIds: [...scope.unitIds],
    fromDateKey: shiftSchedulerDateKeyByDays(scope.fromDateKey, dayOffset),
    toDateKey: shiftSchedulerDateKeyByDays(scope.toDateKey, dayOffset),
    weekStartDateKey: shiftSchedulerDateKeyByDays(scope.weekStartDateKey, dayOffset),
  };
};

export const isSchedulerSingleWeekScope = (
  scope: Pick<SchedulerBootstrapScope, 'fromDateKey' | 'toDateKey' | 'weekStartDateKey'>
): boolean => {
  try {
    return (
      scope.fromDateKey === scope.weekStartDateKey &&
      scope.toDateKey === shiftSchedulerDateKeyByDays(scope.weekStartDateKey, 6)
    );
  } catch {
    return false;
  }
};

export const normalizeSchedulerBootstrapUnitIds = (unitIds: string[]): string[] =>
  Array.from(new Set(unitIds.map(normalizeString).filter(Boolean))).sort();

export const shouldLoadSchedulerTurnoverForScope = ({
  unitId,
  delegatedPublishedOnlyUnitIds,
}: {
  unitId: string | null;
  delegatedPublishedOnlyUnitIds: readonly string[];
}): boolean =>
  typeof unitId === 'string' &&
  unitId.length > 0 &&
  !delegatedPublishedOnlyUnitIds.includes(unitId);

export const buildSchedulerBootstrapScopeKey = (scope: SchedulerBootstrapScope): string =>
  JSON.stringify({
    user: normalizeString(scope.userId) || 'anonymous',
    units: normalizeSchedulerBootstrapUnitIds(scope.unitIds),
    division: normalizeString(scope.divisionId) || null,
    from: normalizeString(scope.fromDateKey),
    to: normalizeString(scope.toDateKey),
    weekStart: normalizeString(scope.weekStartDateKey),
    noteMode: scope.noteMode,
  });

export const isSchedulerBootstrapPayloadForScope = (
  payload: SchedulerBootstrapPayload | null | undefined,
  scope: SchedulerBootstrapScope
): payload is SchedulerBootstrapPayload =>
  Boolean(
    payload &&
      payload.schemaVersion === SCHEDULER_BOOTSTRAP_SCHEMA_VERSION &&
      payload.scopeKey === buildSchedulerBootstrapScopeKey(scope)
  );

export const isSchedulerBootstrapPayload = (value: unknown): value is SchedulerBootstrapPayload => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Partial<SchedulerBootstrapPayload>;
  return (
    payload.schemaVersion === SCHEDULER_BOOTSTRAP_SCHEMA_VERSION &&
    typeof payload.scopeKey === 'string' &&
    typeof payload.userId === 'string' &&
    Array.isArray(payload.unitIds) &&
    typeof payload.fromDateKey === 'string' &&
    typeof payload.toDateKey === 'string' &&
    typeof payload.weekStartDateKey === 'string' &&
    (payload.noteMode === 'draft' || payload.noteMode === 'published') &&
    typeof payload.accessProfileVersion === 'number' &&
    typeof payload.permissionVersion === 'string' &&
    payload.permissionVersion.length > 0 &&
    (typeof payload.pollBaselineAt === 'undefined' || typeof payload.pollBaselineAt === 'string') &&
    typeof payload.scheduleRevision === 'string' &&
    Array.isArray(payload.degradedSections) &&
    payload.degradedSections.every(section => typeof section === 'string') &&
    Boolean(payload.permissions && typeof payload.permissions.canRead === 'boolean') &&
    Boolean(payload.accessModeByUnitId && typeof payload.accessModeByUnitId === 'object') &&
    Object.values(payload.accessModeByUnitId || {}).every(
      mode => mode === 'native' || mode === 'cross_unit_temporary' || mode === 'denied'
    ) &&
    Array.isArray(payload.delegatedPublishedOnlyUnitIds) &&
    payload.delegatedPublishedOnlyUnitIds.every(unitId => typeof unitId === 'string') &&
    Boolean(payload.userDirectoryPermissions && typeof payload.userDirectoryPermissions.canRead === 'boolean' && typeof payload.userDirectoryPermissions.canWrite === 'boolean') &&
    Array.isArray(payload.shifts) &&
    (typeof payload.timePresetUsageShifts === 'undefined' || Array.isArray(payload.timePresetUsageShifts)) &&
    Array.isArray(payload.users) &&
    Array.isArray(payload.positions) &&
    Boolean(payload.weekSettingsByUnitId && typeof payload.weekSettingsByUnitId === 'object') &&
    Boolean(payload.dailyContextByDateKey && typeof payload.dailyContextByDateKey === 'object')
  );
};
